import _isEqual from 'underscore/cjs/isEqual.js'
import QuotesService from '~/services/quotes.service'
import { VERTICAL, ENERGY_VERTICALS } from '~/helpers/vertical.constants'
import { isEmailVerificationRequired } from '~/helpers/email-verification'
const quoteService = new QuotesService()

/*
 * Fetches leads and maps them to quote rows without touching store state,
 * so refreshQuotes can swap the full list atomically.
 */
async function fetchLeadsList (payload = {}) {
  const r = await quoteService.fetchLeads(payload)
  const response = await checkResponse(r, false)
  return response.data?.map(q => {
    q.source = 'Online comparison'
    return q
  }) || []
}

/*
 * Fetches opportunities (including the per-opportunity life-insurance broker
 * quotes, awaited so the buffered list is complete) without touching state.
 */
async function fetchOpportunitiesList (payload = {}) {
  const r = await quoteService.opportunities(payload)
  const response = await checkResponse(r, false)
  const items = response.data?.filter(q => q.vertical !== VERTICAL.LIFE_INSURANCE).map(q => {
    const isEnergyVertical = ENERGY_VERTICALS.includes(q.vertical)
    q.source = 'Expert recommendation'
    if (isEnergyVertical) {
      if (q.online_application === false) {
        q.source = 'Expert recommendation'
      } else {
        q.source = 'Online comparison'
      }
    }
    return q
  }) || []

  const lifeOpportunities = response.data?.filter(q => q.vertical === VERTICAL.LIFE_INSURANCE) || []
  const brokerQuoteGroups = await Promise.all(lifeOpportunities.map(async q => {
    const uniqueIds = await useFetch('/api/life-insurance/agent-quotes', {
      method: 'get',
      params: {
        opportunity_id: q.opportunity_id
      }
    }).then(res => res?.data?.value?.data?.data || [])
    return uniqueIds.map(item => ({
      ...q,
      ...item,
      source: 'Broker Quote'
    }))
  }))

  return [...items, ...brokerQuoteGroups.flat()]
}

const actions = {
  loader (status = true) {
    const layoutStore = useLayoutsStore()
    layoutStore.setPageLoader(status)
  },
  /*
   * Refreshes the full quotes list (leads + opportunities + awaited
   * life-insurance broker quotes) with stale-while-revalidate semantics:
   * the next list is buffered off-state and swapped atomically so the UI
   * never sees a partial list mid-refresh. `quotes_revision` increments only
   * when the payload actually changed (drives the crossfade). On error with
   * cached quotes the stale list is kept; the error is rethrown either way
   * so callers (e.g. the page's interval) keep their error handling.
   *
   * @param {Object} payload - Payload forwarded to both fetches.
   * @param {boolean} [loader=false] - Flag to show or hide the page loader.
   * @returns {Promise<Array>} The current quotes list after the refresh.
   */
  async refreshQuotes (payload = {}, loader = false) {
    if (loader) {
      this.loader(true)
    }
    try {
      const [leads, opportunities] = await Promise.all([
        fetchLeadsList(payload),
        fetchOpportunitiesList(payload)
      ])
      const next = [...leads, ...opportunities]
      this.quotes_verification_required = false
      if (!_isEqual(this.quotes, next)) {
        this.quotes = next
        this.quotes_revision++
      }
      this.quotes_fetched_at = Date.now()
      const hashId = useUserStore().getUserProfile?.hash_id
      if (hashId) {
        this.quotes_user_id = hashId
      }
      return this.quotes
    } catch (error) {
      if (isEmailVerificationRequired(error)) {
        // Unverified/guest member: the API withholds Salesforce quotes until the email
        // is verified. Drop any cached list (it may predate the gate) and let the page
        // show the verify-your-email state rather than an error.
        if (this.quotes.length) {
          this.quotes = []
          this.quotes_revision++
        }
        this.quotes_verification_required = true
        this.quotes_fetched_at = Date.now()
        return this.quotes
      }
      if (this.quotes.length > 0) {
        // eslint-disable-next-line no-console -- diagnostic for ops
        console.warn('[quotesStore] refreshQuotes failed, keeping cached quotes', error?.message || error)
      } else {
        // eslint-disable-next-line no-console -- diagnostic for ops
        console.warn('[quotesStore] refreshQuotes failed with empty cache', error?.message || error)
      }
      throw error
    } finally {
      if (loader) {
        this.loader(false)
      }
    }
  },
  /*
 * Applies life insurance quotes.
 * This function sends a request to the `/api/life-insurance/quotes/apply` endpoint
 * with the provided payload and handles the response.
 * It also manages the loader state during the request.
 *
 * @param {Object} payload - The payload for the quotes application request.
 * @param {boolean} [loader=true] - Flag to show or hide the loader.
 * @returns {Promise<Object>} A promise that resolves with the response data or rejects with an error.
 */
  async applyQuotes (payload = {}, loader = true) {
    if (loader) {
      this.loader(true)
    }
    const r = await useFetch('/api/life-insurance/quotes/apply', {
      method: 'get',
      params: payload
    }).then(res => res)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      }).finally(() => {
        if (loader) {
          this.loader(false)
        }
      })
  },
  /*
 * Requests a callback for life insurance.
 * This function sends a request to the `/api/life-insurance/request-callback` endpoint
 * with the provided payload and handles the response.
 * It also manages the loader state during the request.
 *
 * @param {Object} payload - The payload for the callback request.
 * @param {boolean} [loader=true] - Flag to show or hide the loader.
 * @returns {Promise<Object>} A promise that resolves with the response data or rejects with an error.
 */
  async requestCallback (payload = {}, loader = true) {
    if (loader) {
      this.loader(true)
    }
    const r = await useFetch('/api/life-insurance/request-callback', {
      method: 'get',
      params: payload
    }).then(res => res)
    return checkResponse(r, false)
      .then(response => {
        return Promise.resolve(response.data)
      })
      .catch(error => {
        return Promise.reject(error)
      }).finally(() => {
        if (loader) {
          this.loader(false)
        }
      })
  }
}
export default actions
